import { useMemo } from "react";
import { useLocation } from "react-router-dom";

export interface TomPageContext {
  module: string;
  path: string;
  entityId?: string;
  entityType?: string;
}

/**
 * Detects the current module and entity from the URL.
 * Route formats match actual moduleConfig.ts paths:
 *   /client/:id (singular), /bill/:id (singular), /email/:id (singular)
 *   /opportunities/:id (plural), /calendars/:id (plural)
 *   /p/:id (proposals v2)
 */
export function useTomContext(): TomPageContext {
  const location = useLocation();

  return useMemo(() => {
    const path = location.pathname;
    const segments = path.split("/").filter(Boolean);

    // /opportunities/:id (plural)
    if (segments[0] === "opportunities" && segments[1]) {
      return { module: "opportunities", path, entityId: segments[1], entityType: "opportunity" };
    }
    if (segments[0] === "opportunities") {
      return { module: "opportunities", path };
    }

    // /email/:id (singular) or /emails (list) or /inbox/:id
    if ((segments[0] === "email" || segments[0] === "inbox") && segments[1]) {
      return { module: "emails", path, entityId: segments[1], entityType: "email" };
    }
    if (segments[0] === "emails" || segments[0] === "email" || segments[0] === "inbox") {
      return { module: "emails", path };
    }

    // /p/:id (proposals v2) or /proposals
    if (segments[0] === "p" && segments[1]) {
      return { module: "proposals", path, entityId: segments[1], entityType: "proposal" };
    }
    if (segments[0] === "proposals" || segments[0] === "p") {
      return { module: "proposals", path };
    }

    // /calendars/:id/view
    if (segments[0] === "calendars") {
      return { module: "calendar", path, entityId: segments[1], entityType: "calendar" };
    }

    // /client/:id (singular) or /clients (list)
    if (segments[0] === "client" && segments[1]) {
      return { module: "clients", path, entityId: segments[1], entityType: "client" };
    }
    if (segments[0] === "clients" || segments[0] === "client") {
      return { module: "clients", path };
    }

    // /bill/:id (singular) or /bills (list)
    if (segments[0] === "bill" && segments[1]) {
      return { module: "bills", path, entityId: segments[1], entityType: "bill" };
    }
    if (segments[0] === "bills" || segments[0] === "bill") {
      return { module: "bills", path };
    }

    return { module: "general", path };
  }, [location.pathname]);
}
